import { Kap_Journal, Mutation_Root, Query_Root } from 'src/@types/graphql'
import { ReactElement, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useHashScroll } from 'src/shared/utils/hooks/navigation-hooks'
import { ExpandableCard } from 'src/shared/presentation/ExpandableCard'
import { S } from 'src/shared/styled/S'
import { DeleteIcon, EditIcon, JournalIcon, UploadIcon } from 'src/shared/icons/Icons'
import { DateUtils } from 'src/shared/utils/DateUtils'
import IconButton from '@mui/material/IconButton'
import { ConfirmationModalDialog } from 'src/shared/modal-dialog/ConfirmationModalDialog'
import { UploadModalDialog } from 'src/shared/modal-dialog/UploadModalDialog'
import { ENTITY_TYPE, FILE_TYPE } from 'src/shared/constants/constants'
import { KapJournalCardContent } from 'src/screens/kap/program/journal/journal-card/KapJournalCardContent'
import {
  deleteKapJournalByIdMutation,
  fetchKapJournalFileUUIDsQuery,
} from 'src/screens/kap/program/journal/kapJournalQueries'
import { AxiosResponse } from 'axios'
import { FileService } from 'src/service/axios/FileService'

interface Props {
  id: string
  kapJournal: Kap_Journal
  canEdit: boolean
  onEdit: () => void
  refetch: () => void
}

export const ExpandableKapJournalCard = ({ id, kapJournal, canEdit, onEdit, refetch }: Props): ReactElement => {
  const { state } = useLocation()
  const expandedJournalId = state?.expandedJournalId

  const { getMessage } = useMessageSource()

  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [expanded, setExpanded] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [refetchDocs, setRefetchDocs] = useState(false)

  useEffect(() => {
    const shouldExpand = parseInt(expandedJournalId as string) === kapJournal.id
    setExpanded(shouldExpand)
  }, [kapJournal, expandedJournalId])

  const handleDeleteJournalEntry = () => {
    setDeleteConfirmation(true)
  }

  const handleCancelDeleteJournalEntry = () => {
    setDeleteConfirmation(false)
  }

  const handleConfirmDeleteJournalEntry = () => {
    setDeleteConfirmation(false)
    deleteJournalFiles().then(() => deleteJournalEntry())
  }

  const deleteJournalEntry = async () => {
    const { data } = await urqlClient
      .mutation<{ delete_kap_journal_by_pk: Mutation_Root['delete_kap_journal_by_pk'] }>(deleteKapJournalByIdMutation, {
        id: kapJournal.id,
      })
      .toPromise()

    if (data?.delete_kap_journal_by_pk) {
      refetch()
      notificationService.deleteSuccessful()
    } else {
      notificationService.operationFailed()
    }
  }

  const deleteJournalFiles = async () => {
    const { data } = await urqlClient
      .query<{ file: Query_Root['file'] }>(fetchKapJournalFileUUIDsQuery, {
        kapJournalId: kapJournal.id,
      })
      .toPromise()

    if (data?.file && data.file.length > 0) {
      const deletePromises: Promise<AxiosResponse>[] = []
      data.file.forEach((file) => deletePromises.push(FileService.remove(file.uuid)))
      await Promise.all(deletePromises)
    }
  }

  const onFileSave = () => {
    setExpanded(true)
    setUploadModalOpen(false)
    setRefetchDocs(true)
  }

  useHashScroll(id, true)

  return (
    <>
      <ExpandableCard
        id={id}
        header={
          <S.JournalCard.Content>
            <JournalIcon fontSize="small" />
            <S.JournalCard.ItemContainer>
              <S.JournalCard.ItemTitle variant="subtitle2">{kapJournal.title}</S.JournalCard.ItemTitle>
              <S.JournalCard.ItemDate variant="body2">
                {DateUtils.parseAndFormatDate(kapJournal.journal_date)}
              </S.JournalCard.ItemDate>
              <S.JournalCard.ItemStatusChip
                size="small"
                label={getMessage(`label.program.journal.status.${kapJournal.status}`)}
              />
            </S.JournalCard.ItemContainer>
          </S.JournalCard.Content>
        }
        actions={
          canEdit && (
            <>
              <IconButton color="primary" onClick={onEdit}>
                <EditIcon />
              </IconButton>
              <IconButton color="primary" onClick={() => setUploadModalOpen(true)}>
                <UploadIcon />
              </IconButton>
              <IconButton color="primary" onClick={handleDeleteJournalEntry}>
                <DeleteIcon />
              </IconButton>
              <ConfirmationModalDialog
                open={deleteConfirmation}
                onCancel={handleCancelDeleteJournalEntry}
                onConfirm={handleConfirmDeleteJournalEntry}
              >
                {getMessage('label.delete.confirm')}
              </ConfirmationModalDialog>
            </>
          )
        }
        content={
          <KapJournalCardContent
            kapJournalId={kapJournal.id}
            refetchDocs={refetchDocs}
            resetRefetchDocs={() => setRefetchDocs(false)}
            canEdit={canEdit}
          />
        }
        localExpansion={expanded}
      />

      <UploadModalDialog
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onSave={onFileSave}
        fileType={FILE_TYPE.KAP_JOURNAL}
        entityType={ENTITY_TYPE.KAP_JOURNAL}
        entityId={kapJournal.id}
        acceptEmailTypes
      />
    </>
  )
}
